import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Utensils, Plus, Users, ArrowLeft, UserPlus, 
  ChevronRight, X
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { getGroups, createGroup } from '../lib/api';
import { toast } from 'sonner';

const Groups = () => {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newGroup, setNewGroup] = useState({ name: '', description: '', members: [] });
  const [memberEmail, setMemberEmail] = useState('');
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    fetchGroups();
  }, []);

  const fetchGroups = async () => {
    try {
      const res = await getGroups();
      setGroups(res.data);
    } catch (error) {
      toast.error('Failed to load groups');
    } finally {
      setLoading(false);
    }
  };

  const handleAddMember = () => {
    if (memberEmail && !newGroup.members.includes(memberEmail)) {
      setNewGroup({ ...newGroup, members: [...newGroup.members, memberEmail] });
      setMemberEmail('');
    }
  };

  const handleRemoveMember = (email) => {
    setNewGroup({ ...newGroup, members: newGroup.members.filter(m => m !== email) });
  };

  const handleCreateGroup = async () => {
    if (!newGroup.name) {
      toast.error('Please enter a group name');
      return;
    }
    
    setCreating(true);
    try {
      await createGroup({
        name: newGroup.name,
        description: newGroup.description,
        member_emails: newGroup.members
      });
      toast.success('Group created!');
      setShowCreateModal(false);
      setNewGroup({ name: '', description: '', members: [] });
      fetchGroups();
    } catch (error) {
      toast.error('Failed to create group');
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="min-h-screen" style={{background: 'linear-gradient(135deg, #fff7ed 0%, #fef3c7 50%, #fff7ed 100%)'}}>
      {/* Background */}
      <div className="fixed inset-0 bg-gradient-mesh pointer-events-none" />
      
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 h-16 bg-white/95 backdrop-blur-xl border-b border-orange-100 z-40 flex items-center px-4 lg:px-8 shadow-sm">
        <div className="max-w-6xl mx-auto w-full flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/dashboard" className="p-2 hover:bg-orange-50 rounded-lg transition-colors">
              <ArrowLeft className="w-5 h-5 text-slate-600" />
            </Link>
            <h1 className="text-xl font-semibold font-['Poppins'] text-slate-800">Groups</h1>
          </div>
          
          <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
            <DialogTrigger asChild>
              <Button className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white rounded-full shadow-lg" data-testid="create-group-btn">
                <Plus className="w-4 h-4 mr-2" />
                New Group
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-white border-orange-100 max-w-md">
              <DialogHeader>
                <DialogTitle className="text-xl font-['Poppins'] text-slate-800">Create New Group</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 pt-4">
                <div className="space-y-2">
                  <Label className="text-slate-700">Group Name</Label>
                  <Input
                    placeholder="e.g., Roommates, Trip to Goa"
                    value={newGroup.name}
                    onChange={(e) => setNewGroup({ ...newGroup, name: e.target.value })}
                    className="h-12 bg-white border-slate-200 rounded-xl"
                    data-testid="group-name-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label className="text-slate-700">Description (optional)</Label>
                  <Input
                    placeholder="What's this group for?"
                    value={newGroup.description}
                    onChange={(e) => setNewGroup({ ...newGroup, description: e.target.value })}
                    className="h-12 bg-white border-slate-200 rounded-xl"
                    data-testid="group-description-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label className="text-slate-700">Add Members</Label>
                  <div className="flex gap-2">
                    <Input
                      type="email"
                      placeholder="Enter email address"
                      value={memberEmail}
                      onChange={(e) => setMemberEmail(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleAddMember()}
                      className="h-12 bg-white border-slate-200 rounded-xl flex-1"
                      data-testid="member-email-input"
                    />
                    <Button onClick={handleAddMember} variant="outline" className="h-12 px-4 border-slate-200 hover:bg-orange-50 rounded-xl">
                      <UserPlus className="w-5 h-5" />
                    </Button>
                  </div>
                </div>
                
                {newGroup.members.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {newGroup.members.map((email) => (
                      <div key={email} className="flex items-center gap-2 px-3 py-1.5 bg-orange-100 rounded-full text-sm">
                        <span className="text-orange-700">{email}</span>
                        <button onClick={() => handleRemoveMember(email)} className="text-orange-600 hover:text-red-600">
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                
                <Button
                  onClick={handleCreateGroup}
                  disabled={creating}
                  className="w-full h-12 bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white rounded-xl mt-4 shadow-lg"
                  data-testid="submit-group-btn"
                >
                  {creating ? (
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    'Create Group'
                  )}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </header>

      {/* Main Content */}
      <main className="pt-24 px-4 lg:px-8 pb-8 relative z-10">
        <div className="max-w-6xl mx-auto">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="w-12 h-12 border-4 border-orange-200 border-t-orange-500 rounded-full animate-spin" />
            </div>
          ) : groups.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {groups.map((group, index) => (
                <motion.div
                  key={group.group_id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white/90 backdrop-blur rounded-2xl p-6 card-hover border border-orange-100 shadow-lg"
                  data-testid={`group-card-${index}`}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-orange-100 to-amber-100 flex items-center justify-center">
                      <Users className="w-7 h-7 text-orange-600" />
                    </div>
                    <ChevronRight className="w-5 h-5 text-slate-400" />
                  </div>
                  
                  <h3 className="text-xl font-semibold font-['Poppins'] mb-2 text-slate-800">{group.name}</h3>
                  <p className="text-slate-500 text-sm mb-4 line-clamp-2">{group.description || 'No description'}</p>
                  
                  <div className="flex items-center justify-between">
                    <div className="flex -space-x-2">
                      {group.members?.slice(0, 4).map((member, i) => (
                        <div
                          key={i}
                          className="w-8 h-8 rounded-full bg-gradient-to-br from-orange-500 to-red-500 border-2 border-white flex items-center justify-center text-xs font-medium text-white shadow-md"
                          title={member.name || member.email}
                        >
                          {(member.name || member.email)?.charAt(0).toUpperCase()}
                        </div>
                      ))}
                      {group.members?.length > 4 && (
                        <div className="w-8 h-8 rounded-full bg-slate-200 border-2 border-white flex items-center justify-center text-xs text-slate-600">
                          +{group.members.length - 4}
                        </div>
                      )}
                    </div>
                    <span className="text-sm text-slate-500">{group.members?.length || 0} members</span>
                  </div>
                </motion.div>
              ))}
            </div>
          ) : (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center py-20"
            >
              <div className="w-24 h-24 mx-auto mb-6 rounded-3xl bg-gradient-to-br from-orange-100 to-amber-100 flex items-center justify-center">
                <Users className="w-12 h-12 text-orange-500 opacity-50" />
              </div>
              <h2 className="text-2xl font-semibold font-['Poppins'] mb-2 text-slate-800">No groups yet</h2>
              <p className="text-slate-500 mb-6">Create a group to start splitting bhojan bills with friends</p>
              <Button
                onClick={() => setShowCreateModal(true)}
                className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white rounded-full px-8 shadow-lg"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create Your First Group
              </Button>
            </motion.div>
          )}
        </div>
      </main>
    </div>
  );
};

export default Groups;
